// Push notifications: where a notification leads, and how a screen opened from one finds
// the request it is about. Notifications come from the admin backend (User::notifyInhouse);
// their data.action is the screen to open, e.g. /hr/leave-approvals?id=42.
import { defineComponent, nextTick } from 'vue';
import { toastController } from '@ionic/vue';

/** Window event App.vue fires when a notification arrives while the app is open (detail: its data). */
export const PUSH_EVENT = 'inhouse-push';

/** Android channel for requests and approvals; the backend sends to this id (InhousePushService). */
export const PUSH_CHANNEL = 'inhouse_requests';

const PENDING_KEY = 'push_after_login';

// Older notifications name a screen by key instead of a path.
const LEGACY_ACTIONS: Record<string, string> = {
  transfer_request: '/my-received-gps-device-transfer-request',
  my_transfer_request: '/my-sent-gps-device-transfer-request',
  installation_assignment: '/my-installation-assignments',
  service_assignment: '/my-service-assignments',
  received_iou_requests: '/my-received-iou-requests',
  my_iou_requests: '/my-sent-iou-requests',
  received_bill_requests: '/received-bill-requests',
  my_bills: '/my-bills',
  iou_request_declined: '/my-bills', // sent to the submitter when a bill is declined
};

/** The app route a notification opens, or null when it names none. */
export function pushRoute(data: any): string | null {
  const action = data && data.action;
  if (!action || typeof action !== 'string') return null;
  if (LEGACY_ACTIONS[action]) return LEGACY_ACTIONS[action];
  // Some actions come without the leading slash ("all-sales/AllSale/5"); pushed as-is the
  // router resolved them relative to the current screen.
  return action.startsWith('/') ? action : '/' + action;
}

/** A notification tapped while logged out: remembered so Login.vue can open it after sign-in. */
export function rememberPushRoute(route: string): void {
  window.sessionStorage.setItem(PENDING_KEY, route);
}

export function takePushRoute(): string | null {
  const route = window.sessionStorage.getItem(PENDING_KEY);
  window.sessionStorage.removeItem(PENDING_KEY);
  return route;
}

/**
 * Mixin for list screens a notification opens at one request (?id=). The screen binds
 * :data-push-id="item.id" and :class="pushFocusClass(item.id)" on each card, calls
 * focusPushCard() once its list has loaded, and overrides reloadForPush() to reload the list.
 * The card is then scrolled to and highlighted, and the list reloads when another notification
 * for this screen is opened, or one arrives while the screen is showing.
 */
export const pushFocus = defineComponent({
  data() {
    return {
      pushFocusId: null as string | null,
      pushFocusDone: null as string | null,
      pushScreen: '',
    };
  },
  watch: {
    // Another notification for this same screen: only the query changes, the screen stays.
    '$route.query.id'(id: unknown) {
      if (this.$route.path !== this.pushScreen || id == null) return;
      this.pushFocusId = String(id);
      this.reloadForPush();
    },
  },
  created() {
    this.pushScreen = this.$route.path;
    const id = this.$route.query.id;
    this.pushFocusId = id != null ? String(id) : null;
    window.addEventListener(PUSH_EVENT, this.onPushEvent);
  },
  unmounted() {
    window.removeEventListener(PUSH_EVENT, this.onPushEvent);
  },
  methods: {
    reloadForPush() {
      // Overridden by the screen.
    },
    pushFocusClass(id: unknown) {
      return { 'push-focus': this.pushFocusId !== null && String(id) === this.pushFocusId };
    },
    onPushEvent(event: Event) {
      const route = pushRoute((event as CustomEvent).detail);
      if (route && route.split('?')[0] === this.pushScreen) this.reloadForPush();
    },
    /** goneMessage: shown when the request is no longer in the list (e.g. already handled). */
    async focusPushCard(goneMessage?: string) {
      const id = this.pushFocusId;
      if (!id || this.pushFocusDone === id) return;
      this.pushFocusDone = id;
      await nextTick();
      const card = document.querySelector(`[data-push-id="${id.replace(/["\\]/g, '')}"]`);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (goneMessage) {
        const toast = await toastController.create({ message: goneMessage, duration: 3000, position: 'top' });
        await toast.present();
      }
    },
  },
});
